import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirmationDialog } from "../../../../contexts/ConfirmationDialogContext";
import DialogComponent from "../dialogs/DialogComponent";
import DialogContentWrapper from "../dialogs/DialogContentWrapper";
import SendUserMessageForm from "./SendUserMessageForm";
import {
  EmailRecipient,
  EmailSenderOption,
  MessageDeliveryChannels,
  SenderMode,
} from "./sendUserMessageTypes";

const hasEmailContent = (html: string): boolean => {
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length > 0;
};

interface Props {
  open: boolean;
  onClose: () => void;
  senderMode: SenderMode;
  recipients?: EmailRecipient[];
  isSending: boolean;
  translationNamespace?: string;
  allowAttachments?: boolean;
  onRemoveRecipient?: (id: string) => void;
  onSend: (
    params: MessageDeliveryChannels & {
      subject: string;
      messageHtml: string;
      fromNoReplyAddress: boolean;
      attachments: File[];
    },
  ) => Promise<void>;
}

const SendUserMessageDialog = ({
  open,
  onClose,
  senderMode,
  recipients,
  isSending,
  translationNamespace = "sendEmail",
  allowAttachments = true,
  onRemoveRecipient,
  onSend,
}: Props) => {
  const { t } = useTranslation([translationNamespace, "common"]);
  const { confirm } = useConfirmationDialog();

  const [subject, setSubject] = useState("");
  const [messageHtml, setMessageHtml] = useState("");
  const [selectedSender, setSelectedSender] = useState<EmailSenderOption>("noreply");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sendAsEmail, setSendAsEmail] = useState(true);
  const [sendAsInApp, setSendAsInApp] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubject("");
      setMessageHtml("");
      setSelectedSender("noreply");
      setAttachments([]);
      setSendAsEmail(true);
      setSendAsInApp(false);
    }
  }, [open]);

  const effectiveSender: EmailSenderOption =
    senderMode.type === "fixed" ? senderMode.value : selectedSender;

  const hasDeliveryChannel = sendAsEmail || sendAsInApp;

  const saveDisabled =
    !hasDeliveryChannel ||
    !subject.trim() ||
    !hasEmailContent(messageHtml) ||
    isSending ||
    (recipients !== undefined && recipients.length === 0);

  const handleSave = async () => {
    const confirmKey =
      sendAsEmail && sendAsInApp
        ? "confirmEmailAndInApp"
        : sendAsInApp
          ? "confirmInApp"
          : "confirmEmail";
    const confirmed = await confirm({
      title: t(`${translationNamespace}:dialogTitle`),
      contentText: t(`${translationNamespace}:${confirmKey}`, {
        count: recipients?.length ?? 0,
      }),
      confirmText: t(`${translationNamespace}:confirmSend`),
      cancelText: t("common:cancel"),
    });

    if (!confirmed) return;

    await onSend({
      subject,
      messageHtml,
      fromNoReplyAddress: effectiveSender === "noreply",
      attachments,
      sendAsEmail,
      sendAsInApp,
    });
  };

  return (
    <DialogComponent
      open={open}
      onClose={onClose}
      variant="form"
      hideBackdrop
      sx={{ maxHeight: "calc(100vh - 32px)", overflow: "hidden" }}
      DialogContentProps={{
        sx: {
          overflow: "hidden",
          minHeight: 0,
          maxHeight: "calc(100vh - 32px)",
          flexDirection: "column",
        },
      }}
    >
      <DialogContentWrapper
        title={t(`${translationNamespace}:dialogTitle`)}
        onSave={handleSave}
        onCancel={onClose}
        saveDisabled={saveDisabled}
        isLoading={isSending}
        customSaveButtonLabel={t(`${translationNamespace}:send`)}
        containerSx={{
          minHeight: 0,
          // clip keeps the flex scroll layout, but lets the checkbox hover
          // disc paint into the dialog padding instead of cutting it off.
          overflow: "clip",
          overflowClipMargin: "16px",
          height: "100%",
          flex: 1,
        }}
        childContainerSx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "clip",
          overflowClipMargin: "16px",
          alignContent: "flex-start",
        }}
        buttonsContainerSx={{ flexShrink: 0, marginTop: "1rem" }}
      >
        <SendUserMessageForm
          subject={subject}
          onSubjectChange={setSubject}
          messageHtml={messageHtml}
          onMessageHtmlChange={setMessageHtml}
          senderMode={senderMode}
          selectedSender={effectiveSender}
          onSenderChange={setSelectedSender}
          recipients={recipients}
          onRemoveRecipient={onRemoveRecipient}
          translationNamespace={translationNamespace}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          allowAttachments={allowAttachments}
          sendAsEmail={sendAsEmail}
          sendAsInApp={sendAsInApp}
          onSendAsEmailChange={setSendAsEmail}
          onSendAsInAppChange={setSendAsInApp}
        />
      </DialogContentWrapper>
    </DialogComponent>
  );
};

export default SendUserMessageDialog;
